/**
 * Opening a file the tray has already written to disk (VE-26426).
 *
 * The web platform gives no way to hand a file to the OS — there is no "reveal in
 * folder" and no "open with the default application"; Chrome's own download
 * bubble can do those because it is the browser, not a page. What a page can do
 * is read the file back through the handle it wrote with and show it in a tab.
 *
 * That only helps for formats a browser renders. Which those are is partly ours
 * to decide: a `.csv` handed over as `text/csv` is downloaded again, but the same
 * bytes labelled `text/plain` are displayed. Since the audit log Redact writes
 * beside every output is a CSV, that relabelling is the difference between the
 * action covering half of what a download produces and covering all of it.
 *
 * Genuinely binary formats a browser has no viewer for — a spreadsheet, an
 * archive — are still left out: opening one would download a second copy, which
 * is worse than offering nothing.
 *
 * The handles live here rather than in the downloads duck: they are live objects
 * with no serialisable form, and the duck's business is what to draw.
 */

/**
 * Text formats: displayed only if we relabel them, since the browser downloads
 * `text/csv` and friends. Read as plain text, which is also why relabelling is
 * safe — nothing here is ever handed over as markup.
 */
const TEXT_EXTENSIONS = new Set([
  'csv',
  'txt',
  'log',
  'json',
  'xml',
  'md',
  'srt',
  'vtt',
]);

/**
 * Formats a browser displays under their own type.
 *
 * No SVG, deliberately: it is markup, and a tab renders it as a document that
 * can run script. Redact does not write SVG, so listing it would buy nothing and
 * would make the one entry here that executes.
 */
const NATIVE_VIEWABLE_EXTENSIONS = new Set([
  'pdf',
  'mp4',
  'webm',
  'ogg',
  'ogv',
  'm4v',
  'mp3',
  'wav',
  'm4a',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
]);

const VIEWABLE_EXTENSIONS = new Set([
  ...TEXT_EXTENSIONS,
  ...NATIVE_VIEWABLE_EXTENSIONS,
]);

/** How long a blob URL is kept alive for the new tab to load from. */
const BLOB_URL_TTL_MS = 60_000;

const handles = new Map<string, FileSystemFileHandle>();

const keyOf = (batchId: string, index: number) => `${batchId}:${index}`;

export const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

/** Whether showing this file in a tab would display it rather than re-download it. */
export const isViewableInBrowser = (name: string): boolean =>
  VIEWABLE_EXTENSIONS.has(extensionOf(name));

export const rememberDownloadedFile = (
  batchId: string,
  index: number,
  handle: FileSystemFileHandle
) => {
  handles.set(keyOf(batchId, index), handle);
};

/** Whether the file is still reachable — a cleared batch forgets its handles. */
export const hasDownloadedFile = (batchId: string, index: number): boolean =>
  handles.has(keyOf(batchId, index));

export const forgetDownloadedFiles = (batchIds: readonly string[]) => {
  const dropped = new Set(batchIds);
  [...handles.keys()].forEach((key) => {
    if (dropped.has(key.slice(0, key.lastIndexOf(':')))) {
      handles.delete(key);
    }
  });
};

/** Test seam. */
export const forgetAllDownloadedFiles = () => handles.clear();

/**
 * Show a written file in a new tab.
 *
 * The tab is opened *before* reading the file, not after. Opening it after the
 * `await` would put the call outside the user gesture that started it, and
 * popup blockers would stop it — the button would silently do nothing.
 */
export const openDownloadedFile = async (
  batchId: string,
  index: number
): Promise<boolean> => {
  const handle = handles.get(keyOf(batchId, index));
  if (!handle) {
    return false;
  }
  const tab = window.open('', '_blank');
  if (!tab) {
    return false;
  }
  try {
    const file = await handle.getFile();
    // Relabelled so the browser shows it instead of downloading it again. Only
    // ever to text/plain, so this can never turn a file into something the page
    // would execute.
    const blob = TEXT_EXTENSIONS.has(extensionOf(file.name || ''))
      ? new Blob([file], { type: 'text/plain' })
      : file;
    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    // Revoked on a timer rather than immediately: the tab has to fetch it first.
    window.setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
    return true;
  } catch {
    tab.close();
    return false;
  }
};
